import type { PortalRouter } from "@repo/edge/portal";
import { QueryClient } from "@tanstack/react-query";
import { createTRPCClient, httpLink, TRPCClientError, type TRPCLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";
import { EDGE_URL, session } from "./session";
export const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1 } } });
/** Retry an unauthorized request once with a refreshed bearer token. Upstream failures retain the session. */
export async function authenticatedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status !== 401) return response;
  const headers = new Headers(init?.headers);
  const rejected = headers.get("Authorization")?.replace(/^Bearer /, "");
  if (!rejected) return response;
  const token = await session.accessToken(rejected);
  if (!token) return response;
  headers.set("Authorization", `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}
const patientContextLink: TRPCLink<PortalRouter> =
  () =>
  ({ op, next }) => {
    // Capture before any asynchronous token refresh or transport work.
    const patientId = "patientId" in op.context ? op.context.patientId : session.getProfile()?.selectedPatientId;
    return observable((observer) => {
      const finish = op.type === "mutation" ? session.trackMutation() : () => {};
      const subscription = next({ ...op, context: { ...op.context, patientId } }).subscribe({
        next: (value) => observer.next(value),
        error: (error) => {
          finish();
          observer.error(error);
        },
        complete: () => {
          finish();
          observer.complete();
        },
      });
      return () => {
        finish();
        subscription.unsubscribe();
      };
    });
  };
export const edge = createTRPCClient<PortalRouter>({
  links: [
    patientContextLink,
    httpLink({
      url: `${EDGE_URL}/trpc`,
      fetch: authenticatedFetch,
      headers: async ({ op }) => {
        const token = await session.accessToken();
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        if (typeof op.context.patientId === "string") headers["X-Patient-Id"] = op.context.patientId;
        return headers;
      },
    }),
  ],
});
export async function signOut() {
  session.clear();
  queryClient.clear();
}
export class PortalAccessDeniedError extends Error {
  constructor() {
    super("Portal access is not currently approved. Contact your care team.");
    this.name = "PortalAccessDeniedError";
  }
}
export async function restoreProfile() {
  if (await session.accessToken()) {
    try {
      const profile = await edge.profile.query(undefined, { context: { patientId: undefined } });
      queryClient.clear();
      session.setProfile(profile);
    } catch (error) {
      if (error instanceof TRPCClientError && error.data?.code === "FORBIDDEN") {
        await signOut();
        throw new PortalAccessDeniedError();
      }
      throw error;
    }
  }
}

export async function switchPatient(patientId: string) {
  if (session.getPendingMutations()) throw new Error("Please wait for your changes to finish saving");
  await queryClient.cancelQueries();
  if (session.getPendingMutations()) throw new Error("Please wait for your changes to finish saving");
  queryClient.clear();
  session.selectPatient(patientId);
}
